import { useGiveawayStats, useGiveawayUtils } from '@src/hooks/useGiveaways';
import { Link } from '@tanstack/react-router';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { RaffleV2, RaffleV2Type } from 'jigsaw';
import { HStack, Skeleton, Spinner, Text, VStack } from 'ui_2';
import { GiveawayTimeRemaining } from './GiveawayTimeRemaining';
import { ArcadeTicketIcon } from '../ArcadeTicketIcon';
import { Route as index_route } from '@routes/index';
import { Route as giveaway_ad_route } from '@routes/giveaways/$programId/ad';
import PuzzleWalletSponsorRow from '@assets/sponsors/puzzle_wallet/row_bg.png';
import { useMemo, useState } from 'react';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { getDeeplinkBrowserUrl } from '@puzzlehq/frontend-utils';
import { router } from '@src/providers/router';

dayjs.extend(LocalizedFormat);

export const GiveawayView = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const { isGiveawayClosed, isGiveawayWinner } = useGiveawayUtils(giveaway);
  const { userGiveawayEntriesQuery, hasPendingEntries } =
    useGiveawayStats(giveaway);

  const img = useMemo(() => {
    if (giveaway.type === RaffleV2Type.product) {
      return (
        giveaway.productSku.bigImageURL ??
        giveaway.productSku.service.bigLogoImageURL
      );
    } else if (giveaway.type === RaffleV2Type.eth) {
      return giveaway.productSku.bigImageURL ?? PuzzleWalletSponsorRow;
    }
    return giveaway?.sponsor.name === 'Puzzle Wallet'
      ? PuzzleWalletSponsorRow
      : giveaway?.sponsor.arcadeRowBackgroundImageExtensionURL;
  }, [JSON.stringify(giveaway)]);

  const userEntries = userGiveawayEntriesQuery.data;

  return (
    <VStack className={`w-full rounded-lg border border-gray-100`}>
      <div className='relative overflow-hidden rounded-t-[7px] border-b border-gray-100'>
        <img
          src={img}
          onLoad={() => setIsLoaded(true)}
          alt={`${giveaway.title} background`}
          className='h-[100px] w-full object-cover'
        />
        {isGiveawayClosed && (
          <div className='absolute inset-0 bg-gray-500/50' />
        )}
        {!isLoaded && <Skeleton className='h-full w-full' />}
        <div className='absolute left-1 top-1 flex'>
          <GiveawayTimeRemaining giveaway={giveaway} />
        </div>
        {userEntries !== undefined && userEntries > 0 && !isGiveawayWinner && (
          <HStack className='absolute bottom-1 right-1 flex items-center gap-2 rounded-full border border-purple-200 bg-purple-100 px-2'>
            <Text font='sm'>
              {`${userEntries} ${userEntries > 1 ? 'Entries' : 'Entry'}`}
            </Text>
            {hasPendingEntries && <Spinner className='h-3 w-3 text-gray-500' />}
          </HStack>
        )}
      </div>
      <VStack className='w-full p-2'>
        <Text font='xs'>{giveaway.title}</Text>
        <Text font='xs'>{giveaway.prizeDescription}</Text>
        <HStack className='items-center gap-2 '>
          <ArcadeTicketIcon />
          <Text font='xs'>{giveaway.ticketPrice}</Text>
        </HStack>
      </VStack>
    </VStack>
  );
};

export const Giveaway = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { isPuzzleWalletWebview } = useArcadeStoreState();

  const giveaway_link_route = useMemo(
    () =>
      `${window.location.origin}${
        router.buildLocation({
          to: giveaway_ad_route.to,
          params: {
            programId: giveaway.programId,
          },
          search: {
            showHeader: false,
            showAccountAndBalance: false,
            giveawayId: giveaway.giveawayId,
          },
        }).href
      }`,
    [giveaway_ad_route.to.toString(), giveaway.programId, giveaway.giveawayId]
  );

  const deeplink_route = getDeeplinkBrowserUrl(
    giveaway_link_route,
    'fullScreenCover'
  );

  if (isPuzzleWalletWebview) {
    return (
      <a href={deeplink_route} target='_blank'>
        <GiveawayView giveaway={giveaway} />
      </a>
    );
  }

  return (
    <Link
      className='w-full max-w-[250px]'
      to={giveaway_ad_route.to}
      from={index_route.fullPath}
      params={{
        programId: giveaway.programId,
      }}
      search={{
        showHeader: true,
        giveawayId: giveaway.giveawayId,
        showAccountAndBalance: true,
      }}
    >
      <GiveawayView giveaway={giveaway} />
    </Link>
  );
};
